import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage } from '../../../context/LanguageContext.js';
import ButtonWithLoader from '../../../components/Button/ButtonLoader/ButtonLoader.js';
import { authAPI } from '../../../api/api.js';
import { resolvePostLoginPath } from '../../../Utils/postLoginNavigation.js';
import AuthBrand from '../AuthBrand/AuthBrand.jsx';

const VerifyEmailCode = () => {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [resendMessage, setResendMessage] = useState('');
    const { verifyEmailCode } = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const email = location.state?.email || searchParams.get('email') || '';
    const selectedPlan = location.state?.plan || searchParams.get('plan');
    const selectedInterval =
        location.state?.interval === 'yearly' || searchParams.get('interval') === 'yearly'
            ? 'yearly'
            : 'monthly';
    const maskedEmail = location.state?.maskedEmail || email;

    useEffect(() => {
        if (!email) {
            navigate('/login');
            return;
        }

        if (location.state?.codeSent) {
            setResendMessage(t('auth.verifyEmailCode.codeSent'));
        }
    // Only run when the page loads with route state / query params.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await verifyEmailCode(email, code);

            if (result.requiresPasswordChange) {
                navigate(await resolvePostLoginPath({ passwordChangeRequired: true }));
                return;
            }

            const billingParams = new URLSearchParams({ interval: selectedInterval });
            if (selectedPlan) {
                billingParams.set('plan', selectedPlan);
            }
            navigate(`/select-plan?${billingParams.toString()}`);
        } catch (err) {
            setError(err.response?.data?.message || t('auth.verifyEmailCode.failed'));
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResendMessage('');
        setError('');
        setResendLoading(true);

        try {
            const response = await authAPI.resendVerificationCode(email);
            setResendMessage(response.data?.message || t('auth.verifyEmailCode.resendSuccess'));
        } catch (err) {
            setError(err.response?.data?.message || t('auth.verifyEmailCode.resendFailed'));
        } finally {
            setResendLoading(false);
        }
    };

    if (!email) {
        return null;
    }

    return (
      <div className="auth-page">
          <AuthBrand corner to="/login" />
        <div className=" auth-shell container">
          <form onSubmit={handleSubmit} className="auth-card auth-verify-form">
            <div className="auth-verify-header">
              <p className="auth-eyebrow">{t('auth.verifyEmailCode.eyebrow')}</p>
              <h1 className="auth-title">{t('auth.verifyEmailCode.title')}</h1>
              <p className="auth-subtitle auth-verify-subtitle">
                {t('auth.verifyEmailCode.subtitle', { email: maskedEmail })}
              </p>
            </div>

            {error && <div className="auth-message auth-message--error">{error}</div>}
            {resendMessage && (
              <div className="auth-message auth-message--success" role="status">
                {resendMessage}
              </div>
            )}

            <div className="auth-code-input-group">
              <input
                type="text"
                dir="ltr"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength="6"
                pattern="[0-9]{6}"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="auth-code-input"
                required
                autoFocus
              />
            </div>

            <div className="auth-action-box">
              <ButtonWithLoader
                text={t('auth.verifyEmailCode.submit')}
                loading={loading}
                type="submit"
              />
            </div>

            <div className="auth-helper-note">{t('auth.verifyEmailCode.helper')}</div>

            <button
              type="button"
              className="auth-secondary-button"
              onClick={handleResend}
              disabled={resendLoading}
            >
              {resendLoading
                ? t('auth.verifyEmailCode.resending')
                : t('auth.verifyEmailCode.resend')}
            </button>

            <button
              type="button"
              className="auth-secondary-button"
              onClick={() => navigate('/login')}
            >
              {t('auth.verifyEmailCode.backToLogin')}
            </button>
          </form>
        </div>
      </div>
    );
};

export default VerifyEmailCode;
